// src/components/common/InviteManagerModal.jsx
import React, { useState, useCallback, useEffect } from "react";
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ModalCloseButton,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Heading,
  Text,
  HStack,
  Input,
  Button,
  useToast,
  useColorMode,
  Checkbox,
  VStack,
  Box,
  Skeleton,
  SkeletonText,
  IconButton,
} from "@chakra-ui/react";
import Translate from "../LanguageTranslation/Translate";
import {
  sendInvite,
  sentInvites,
  getAvailableResources,
  removeUserFromResource, // 1. Import the new API function
  leaveResource,
} from "../../api/invites";
import { DeleteIcon } from "@chakra-ui/icons";
import { MinusIcon, EditIcon } from "@chakra-ui/icons";
import { addUserToResource } from "../../api/invites";

/**
 * Re-usable "Invite & Manage Users" modal.
 *
 * Props
 * ─ tripId   (string, required)
 * ─ isOpen  (bool,  required)
 * ─ onClose (func,  required)
 */
const InviteManagerModal = ({ tripId, isOpen, onClose, availableResource }) => {
  /* ------------------------------------------------------------ */
  /*                       INTERNAL STATE                         */
  /* ------------------------------------------------------------ */
  const { colorMode } = useColorMode();
  const toast = useToast();

  // Invite lists
  const [accepted, setAccepted] = useState([]);
  const [pending, setPending] = useState([]);
  const [declined, setDeclined] = useState([]);
  const [accountCreation, setAccountCreation] = useState([]);

  // Available resources
  const [availableResources, setAvailableResources] = useState([]);
  const [resourceDetails, setResourceDetails] = useState({});
  const [tripName, setTripName] = useState("");
  const [selectedResources, setSelectedResources] = useState([]);
  const [loadingResources, setLoadingResources] = useState(false);

  // Send-invite helpers
  const [email, setEmail] = useState("");
  const [sending, setSending] = useState(false);
  const [removingUserId, setRemovingUserId] = useState(null); // Track which user is being removed
  const [isTripOwner, setIsTripOwner] = useState(false); // Track if current user is trip owner
  const [removingResource, setRemovingResource] = useState({}); // Track which resource is being removed per invite

  // Add state for selected user and resource type for removal
  const [selectedUserForRemoval, setSelectedUserForRemoval] = useState(null);
  const [selectedResourceTypesForRemoval, setSelectedResourceTypesForRemoval] = useState([]);
  const [leavingResources, setLeavingResources] = useState(false);

  // Add state for edit modal
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [editTargetInvite, setEditTargetInvite] = useState(null);
  const [editResourceSelection, setEditResourceSelection] = useState([]);

  /* ------------------------------------------------------------ */
  /*        Select all resources by default for removal           */
  /* ------------------------------------------------------------ */
  useEffect(() => {
    if (!isOpen) return;
    const userEmail =
      typeof window !== "undefined" ? localStorage.getItem("email") : null;
    const myInvite = [
      ...accepted,
      ...pending,
      ...declined,
      ...accountCreation,
    ].find((i) => i.invited_email === userEmail);
    if (myInvite && Array.isArray(myInvite.resource_types)) {
      setSelectedResourceTypesForRemoval(myInvite.resource_types);
    }
  }, [isOpen, accepted, pending, declined, accountCreation]);

  /* ------------------------------------------------------------ */
  /*                       DATA OPERATIONS                        */
  /* ------------------------------------------------------------ */
  const loadAvailableResources = useCallback(async () => {
    setLoadingResources(true);
    try {
      const { data } = await getAvailableResources(tripId);
      // console.log("loadAvailableResources", data);
      if (data?.success) {
        const resources = data.data?.available_resources || [];
        setAvailableResources(resources);
        setResourceDetails(data.data?.resource_details || {});
        setTripName(data.data?.trip_name || "");

        // Select all available resources by default
        setSelectedResources(resources);
      }
    } catch (err) {
      console.error("Error loading available resources:", err);
    } finally {
      setLoadingResources(false);
    }
  }, [tripId, availableResource]);

  const loadInvites = useCallback(async () => {
    try {
      const { data } = await sentInvites(tripId);
      // console.log("loadInvites", data);
      const list = (data?.data.invites ?? []).filter(Boolean); // Filter out null/undefined
      setAccepted(list.filter((i) => i.status === "accepted"));
      setPending(list.filter((i) => i.status === "pending"));
      setDeclined(list.filter((i) => i.status === "rejected"));
      setAccountCreation(
        list.filter((i) => i.status === "account_creation_sent")
      );
      setIsTripOwner(!!data?.data?.trip_owner); // Set trip owner state
    } catch (err) {
      toast({
        title: "Error loading invites",
        status: "error",
        duration: 4000,
        isClosable: true,
      });
    }
  }, [tripId, toast]);

  const handleResourceToggle = useCallback((resource) => {
    setSelectedResources((prev) => {
      if (prev.includes(resource)) {
        return prev.filter((r) => r !== resource);
      } else {
        return [...prev, resource];
      }
    });
  }, []);

  const send = useCallback(async () => {
    if (!email) return;
    if (selectedResources.length === 0) {
      toast({
        title: "Error",
        description: "Please select at least one resource to share",
        status: "error",
        duration: 4000,
        isClosable: true,
      });
      return;
    }

    setSending(true);
    try {
      const { data } = await sendInvite(tripId, email, selectedResources);
      toast({
        title: "Success",
        description: data?.message || "Invitation sent",
        status: "success",
        duration: 3000,
        isClosable: true,
      });
      setEmail("");
      await loadInvites();
    } catch (err) {
      toast({
        title: err?.response?.data?.message || "Error",
        status: "error",
        duration: 4000,
        isClosable: true,
      });
    } finally {
      setSending(false);
    }
  }, [tripId, email, selectedResources, loadInvites, toast]);

  // Update handleRemoveUser to accept resourceTypes argument
  const handleRemoveUser = useCallback(
    async (inv, resourceTypes) => {
      setRemovingUserId(inv._id);
      try {
        // For remove button, only send "itinerary" in the payload
        const res = await removeUserFromResource(
          tripId,
          inv.invited_email,
          "itinerary"
        );
        toast({
          title: "User removed",
          description: res?.data?.message || "User removed successfully.",
          status: "success",
          duration: 3000,
          isClosable: true,
        });
        await loadInvites();
      } catch (err) {
        toast({
          title: err?.response?.data?.message || "Error removing user",
          description: err?.response?.data?.message,
          status: "error",
          duration: 4000,
          isClosable: true,
        });
      } finally {
        setRemovingUserId(null);
      }
    },
    [tripId, loadInvites, toast]
  );

  // Handler to remove a specific resource from a user
  const handleRemoveResource = useCallback(
    async (inv, resourceType) => {
      const key = `${inv._id}_${resourceType}`;
      setRemovingResource((prev) => ({ ...prev, [key]: true }));
      try {
        // Use leaveResource API function
        const res = await leaveResource(tripId, resourceType);
        const data = res?.data;
        if (res?.status === 200 && data?.success) {
          toast({
            title: "Resource removed",
            description: data?.message || "Resource removed successfully.",
            status: "success",
            duration: 3000,
            isClosable: true,
          });
          await loadInvites();
        } else {
          throw new Error(data?.message || "Error removing resource");
        }
      } catch (err) {
        toast({
          title: err?.message || "Error removing resource",
          status: "error",
          duration: 4000,
          isClosable: true,
        });
      } finally {
        setRemovingResource((prev) => {
          const newState = { ...prev };
          delete newState[key];
          return newState;
        });
      }
    },
    [tripId, toast, loadInvites]
  );

  // Handler to open edit modal
  const handleEditResources = (invite) => {
    setEditTargetInvite(invite);
    setEditResourceSelection(invite.resource_types || []);
    setEditModalOpen(true);
  };
  // Handler to toggle resource in edit modal
  const handleEditResourceToggle = async (resource) => {
    if (editResourceSelection.includes(resource)) {
      // If unchecking, call handleRemoveUser for this resource
      if (editTargetInvite) {
        // Call removeUserFromResource directly instead of handleRemoveUser to avoid setting removingUserId
        try {
          const res = await removeUserFromResource(tripId, editTargetInvite.invited_email, resource);
          const data = res?.data;
          if (res?.status === 200 && data?.success) {
            toast({
              title: data?.message || "Resource removed successfully",
              status: "success",
              duration: 3000,
              isClosable: true,
            });
          } else {
            throw new Error(data?.message || "Error removing resource");
          }
          await loadInvites();
        } catch (err) {
          toast({
            title: err?.response?.data?.message || err?.message || "Error removing resource",
            status: "error",
            duration: 3000,
            isClosable: true,
          });
        }
      }
      setEditResourceSelection((prev) => prev.filter((r) => r !== resource));
    } else {
      // If checking, call addUserToResource for this resource
      if (editTargetInvite) {
        try {
          const res = await addUserToResource(
            tripId,
            editTargetInvite.invited_email,
            resource
          );
          const data = res?.data;
          if (res?.status === 200 && data?.success) {
            toast({
              title: data?.message || "Resource added successfully",
              status: "success",
              duration: 3000,
              isClosable: true,
            });
          } else {
            throw new Error(data?.message || "Error adding resource");
          }
          await loadInvites();
        } catch (err) {
          toast({
            title: err?.response?.data?.message || err?.message || "Error adding resource",
            status: "error",
            duration: 3000,
            isClosable: true,
          });
        }
      }
      setEditResourceSelection((prev) => [...prev, resource]);
    }
  };
  // Handler to save resource changes
  const handleSaveResourceEdit = async () => {
    if (!editTargetInvite) return;
    try {
      await removeUserFromResource(
        tripId,
        editTargetInvite.invited_email,
        editResourceSelection
      );
      toast({
        title: "Resources updated",
        status: "success",
        duration: 3000,
        isClosable: true,
      });
      setEditModalOpen(false);
      setEditTargetInvite(null);
      await loadInvites();
    } catch (err) {
      toast({
        title: err?.response?.data?.message || "Error updating resources",
        status: "error",
        duration: 4000,
        isClosable: true,
      });
    }
  };

  /* ------------------------------------------------------------ */
  /*               LOAD DATA WHENEVER THE MODAL OPENS             */
  /* ------------------------------------------------------------ */
  useEffect(() => {
    if (isOpen) {
      loadAvailableResources();
      loadInvites();
    }
  }, [isOpen, loadAvailableResources, loadInvites]);

  /* ------------------------------------------------------------ */
  /*                           RENDER                             */
  /* ------------------------------------------------------------ */
  return (
    <Modal isOpen={isOpen} onClose={onClose} size={{ base: "sm", md: "4xl" }} scrollBehavior="inside">
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>
          {isTripOwner ? (
            <Translate text="Invite & Manage Users" />
          ) : (
            <Translate text="Itinerary Collaborators" />
          )}
        </ModalHeader>
        <ModalCloseButton />

        <ModalBody overflowY="auto">
          {/* ─ Available Resources ─────────────────────────────── */}
          {isTripOwner && (
            <>
              <Box mb={6}>
                <Heading fontSize="md" mb={2}>
                  <Translate text="Share" />
                </Heading>
                <Text mb={4}>
                  <Translate text="Choose which resources you want to share with this person:" />
                </Text>
                <Heading fontSize="md" mb={3}>
                  <Translate text="Available Resources" />
                </Heading>

                {loadingResources ? (
                  <VStack spacing={2} align="stretch">
                    {[1, 2, 3].map((index) => (
                      <HStack key={index} spacing={3}>
                        <Skeleton height="16px" width="16px" />
                        <SkeletonText noOfLines={1} width="200px" />
                      </HStack>
                    ))}
                  </VStack>
                ) : availableResources.length > 0 ? (
                  <VStack spacing={2} align="stretch">
                    {availableResources.map((resource) => {
                      const details = resourceDetails[resource];
                      const isItinerary =
                        resource.toLowerCase() === "itinerary";
                      return (
                        <Checkbox
                          key={resource}
                          isChecked={
                            isItinerary
                              ? true
                              : selectedResources.includes(resource)
                          }
                          onChange={
                            isItinerary
                              ? undefined
                              : () => handleResourceToggle(resource)
                          }
                          colorScheme="green"
                          // Do not set isDisabled for itinerary, so it doesn't look blurred
                        >
                          <Text fontSize="sm" mb={0}>
                            {details?.name || resource}
                          </Text>
                        </Checkbox>
                      );
                    })}
                  </VStack>
                ) : (
                  <Text fontSize="sm" color="gray.500">
                    <Translate text="No resources available" />
                  </Text>
                )}
              </Box>

              {/* ─ Invite form ─────────────────────────────── */}
              <Heading fontSize="md" mb={2}>
                <Translate text="Send Invite" />
              </Heading>
              <Text mb={4}>
                <Translate text="Enter the email address of the person you'd like to invite:" />
              </Text>

              <HStack mb={6}>
                <Input
                  placeholder="email@example.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  size={{ base: "sm", md: "md" }}
                  borderRadius="lg"
                />
                <Button
                  onClick={send}
                  isLoading={sending}
                  colorScheme="customGreen"
                  size={{ base: "sm", md: "md" }}
                >
                  <Translate text="Send Invite" />
                </Button>
              </HStack>
            </>
          )}

          {/* ─ Remove Resource Access ─────────────────────────────── */}
          {!isTripOwner && (
            <Box mb={6}>
              <Heading fontSize="md" mb={2}>
                <Translate text="Remove Resource Access" />
              </Heading>
              <Text mb={4}>
                <Translate text="Select a resource type to remove your access. If you remove access to 'Itinerary', access to all three resources will be removed." />
              </Text>
              <VStack mb={2} align="start">
                <Text fontWeight="bold" mb={0}>
                  <Translate text="Resource:" />
                </Text>
                {(() => {
                  const userEmail =
                    typeof window !== "undefined"
                      ? localStorage.getItem("email")
                      : null;
                  const myInvite = [
                    ...accepted,
                    ...pending,
                    ...declined,
                    ...accountCreation,
                  ].find((i) => i.invited_email === userEmail);
                  if (!myInvite || !Array.isArray(myInvite.resource_types))
                    return null;
                  return myInvite.resource_types.map((resource) => {
                    const formattedResource = resource
                      .split("_")
                      .map(
                        (word) => word.charAt(0).toUpperCase() + word.slice(1)
                      )
                      .join(" ");
                    return (
                      <Checkbox
                        key={resource}
                        isChecked={selectedResourceTypesForRemoval.includes(resource)}
                        onChange={() => {
                          setSelectedResourceTypesForRemoval((prev) =>
                            prev.includes(resource)
                              ? prev.filter((r) => r !== resource)
                              : [...prev, resource]
                          );
                        }}
                        colorScheme="green"
                      >
                        {formattedResource}
                      </Checkbox>
                    );
                  });
                })()}
              </VStack>
              <Button
                colorScheme="red"
                mt={2}
                isDisabled={selectedResourceTypesForRemoval.length === 0 || leavingResources}
                isLoading={leavingResources}
                onClick={async () => {
                  setLeavingResources(true);
                  const userEmail =
                    typeof window !== "undefined"
                      ? localStorage.getItem("email")
                      : null;
                  const myInvite = [
                    ...accepted,
                    ...pending,
                    ...declined,
                    ...accountCreation,
                  ].find((i) => i.invited_email === userEmail);
                  if (!myInvite) {
                    setLeavingResources(false);
                    return;
                  }
                  if (selectedResourceTypesForRemoval.includes("itinerary")) {
                    await handleRemoveResource(myInvite, "itinerary");
                  } else {
                    for (const resource of selectedResourceTypesForRemoval) {
                      await handleRemoveResource(myInvite, resource);
                    }
                  }
                  setSelectedResourceTypesForRemoval([]);
                  setLeavingResources(false);
                }}
              >
                <Translate text="Leave" />
              </Button>
            </Box>
          )}

          {/* ─ Status table ─────────────────────────────── */}
          <Heading fontSize="md" mb={3}>
            <Translate text="Itinerary Participants" />
          </Heading>

          {[...accepted, ...pending, ...declined, ...accountCreation].length ===
          0 ? (
            <Text>
              <Translate text="Nothing yet." />
            </Text>
          ) : (
            <Table size="sm">
              <Thead>
                <Tr>
                  <Th>
                    <Translate text="Name" />
                  </Th>
                  <Th>
                    <Translate text="Email" />
                  </Th>
                  <Th>
                    <Translate text="Resources Access" />
                  </Th>
                  {isTripOwner && (
                    <>
                      <Th>
                        <Translate text="Status" />
                      </Th>
                      <Th>
                        <Translate text="Edit" />
                      </Th>

                      <Th>
                        <Translate text="Actions" />
                      </Th>
                    </>
                  )}
                </Tr>
              </Thead>
              <Tbody>
                {[...accepted, ...pending, ...declined, ...accountCreation].map(
                  (inv) => {
                    const color =
                      inv.status === "accepted"
                        ? "#286328"
                        : inv.status === "pending"
                        ? "orange.400"
                        : inv.status === "account_creation_sent"
                        ? "blue.400"
                        : "red.500";

                    // Format status: replace underscores with spaces and capitalize each word
                    const formattedStatus = inv.status
                      .split("_")
                      .map(
                        (word) => word.charAt(0).toUpperCase() + word.slice(1)
                      )
                      .join(" ");

                    return (
                      <Tr key={inv._id}>
                        <Td>{inv.invited_username}</Td>
                        <Td>{inv.invited_email}</Td>
                        <Td>
                          <Text fontSize="sm" mb={0}>
                            {inv.resource_types
                              ? inv.resource_types
                                  .map((resource) =>
                                    resource
                                      .split("_")
                                      .map(
                                        (word) =>
                                          word.charAt(0).toUpperCase() +
                                          word.slice(1)
                                      )
                                      .join(" ")
                                  )
                                  .join(", ")
                              : "Itinerary"}
                          </Text>
                        </Td>
                        {isTripOwner && (
                          <>
                            <Td>
                              <Text color={color} textTransform="none" mb={0}>
                                {formattedStatus}
                              </Text>
                            </Td>
                            <Td>
                              <IconButton
                                icon={<EditIcon />}
                                size="xs"
                                colorScheme="blue"
                                variant="ghost"
                                aria-label="Edit resource access"
                                onClick={() => handleEditResources(inv)}
                              />
                            </Td>

                            <Td>
                              <Button
                                size="xs"
                                colorScheme="red"
                                variant="outline"
                                isLoading={removingUserId === inv._id}
                                onClick={() =>
                                  handleRemoveUser(inv, inv.resource_types)
                                }
                              >
                                <Translate text="Remove" />
                              </Button>
                            </Td>
                          </>
                        )}
                      </Tr>
                    );
                  }
                )}
              </Tbody>
            </Table>
          )}
        </ModalBody>
      </ModalContent>

      {/* Edit Resource Modal */}
      <Modal
        isOpen={editModalOpen}
        onClose={() => setEditModalOpen(false)}
        size={{ base: "sm", md: "md" }}
        isCentered
      >
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>
            <Translate text="Edit Resource Access" />
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <VStack align="start" spacing={3}>
              {["itinerary", "budget_planner", "packing_list"].map(
                (resource) => (
                  <Checkbox
                    key={resource}
                    isChecked={editResourceSelection.includes(resource)}
                    onChange={() => handleEditResourceToggle(resource)}
                    colorScheme="green"
                  >
                    {resource
                      .split("_")
                      .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
                      .join(" ")}
                  </Checkbox>
                )
              )}
            </VStack>
          </ModalBody>
          <ModalFooter>
            {/* <Button
              colorScheme="customGreen"
              mr={3}
              onClick={handleSaveResourceEdit}
              isDisabled={editResourceSelection.length === 0}
            >
              <Translate text="Save" />
            </Button> */}
            <Button variant="ghost" onClick={() => setEditModalOpen(false)}>
              <Translate text="Close" />
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Modal>
  );
};

export default InviteManagerModal;
